import { useBreakpoint } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Loader from '../../components/Loader';
import { isProd } from '../../config';
import { MKT_HOME } from '../../constants/urls';
import {
  CreateAffiliateTemporaryInfoMutation,
  ErrorCodeEnum,
  TemporaryData,
  useCreateAffiliateTemporaryInfoMutation,
  useTemporaryInfoLazyQuery,
} from '../../gql/generated/graphql';
import { useFlowCountry } from '../../hooks';
import { CookieKeys, clearCookie, useCookie } from '../../hooks/useCookie';
import hotjar, { HotjarEvents } from '../../services/analytics/hotjar';
import { identify } from '../../services/analytics/rudderstack';
import { resetAuth } from '../../utils/auth';
import { getCampaignTraits } from '../../utils/campaignParams';
import AffiliateSinglePage from './components/AffiliateSinglePage';
import { AFFILIATE_PARAMS } from './utils';

const AffiliateEntry = () => {
  const [guid, setGuid] = useCookie<string>(CookieKeys.GUID_KEY);
  const [showDecoder, setShowDecoder] = useState(false);
  const [tempInfoData, setTempInfoData] = useState<TemporaryData>();

  const { search } = useLocation();
  const device = useBreakpoint();
  const country = useFlowCountry();

  const [getTempInfo] = useTemporaryInfoLazyQuery();
  const [createTempInfo] = useCreateAffiliateTemporaryInfoMutation({
    context: {
      isErrorHandled: true,
    },
  });

  const returnHome = () => {
    window.location.href = MKT_HOME;
  };

  useEffect(() => {
    hotjar.track(HotjarEvents.Affiliate);
  }, []);

  const initialize = async (affiliateExternalId: string) => {
    if (guid) {
      const tempInfo = await getTempInfo({
        variables: { id: guid as string },
      });

      if (tempInfo.error?.graphQLErrors?.[0]?.extensions?.code === ErrorCodeEnum.ResetGuidError) {
        resetAuth({ removeGuid: true });
        returnHome();
        return;
      }

      if (tempInfo.data?.temporaryInfo?.data?.affiliate_external_id === affiliateExternalId) {
        setTempInfoData(tempInfo.data.temporaryInfo.data);
        setShowDecoder(true);
        identify({ guid });
        hotjar.identify(null, {
          temp_info_id: guid,
        });
        return;
      }
    }

    clearCookie(CookieKeys.QUERY_PARAMS);
    const tempInfoMutation: CreateAffiliateTemporaryInfoMutation = await new Promise(
      (resolve, reject) =>
        createTempInfo({
          variables: {
            device_type: device,
            query_params: search,
            country,
          },
          onCompleted: resolve,
          onError: () => {
            returnHome();
            reject();
          },
        }),
    );

    const newGuid = tempInfoMutation.createAffiliateTemporaryInfo?.id as string;
    setGuid(newGuid);

    setTempInfoData(tempInfoMutation.createAffiliateTemporaryInfo?.data);
    clearCookie(CookieKeys.PREQUAL_PARAMS);
    identify({ guid: newGuid, ...getCampaignTraits(search) });
    hotjar.identify(null, {
      temp_info_id: newGuid,
    });

    setShowDecoder(true);
  };

  const isInitialized = useRef(false);
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      const params = new URLSearchParams(search);
      const affiliateExternalId = params.get(AFFILIATE_PARAMS.AFFILIATE_EXTERNAL_ID);

      if (affiliateExternalId) {
        initialize(affiliateExternalId);
      } else if (isProd) {
        returnHome();
      } else {
        setShowDecoder(true);
      }
    }
  }, [search]);

  if (!showDecoder) {
    return <Loader />;
  }

  return <AffiliateSinglePage affiliateTempInfoData={tempInfoData} />;
};

export default AffiliateEntry;
